import React, { useState, useEffect } from 'react'
import {Calculator as CalcIcon, TrendingUp, AlertCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useSavedEstimates } from '@/hooks/useSavedEstimates'
import { CalculatorProps, ValidationErrors, Estimate, CalculatorState } from './calculator/types'
import { countryConfigs } from './calculator/constants'
import { getMaterialOptions, validateZipCode, validateSquareFootage, calculateEstimate } from './calculator/calculatorUtils'
import { downloadPDF, sharePDFViaEmail } from './calculator/PDFGenerator'
import { handlePrint } from './calculator/PrintGenerator'
import FormInputs from './calculator/FormInputs'
import EstimateResults from './calculator/EstimateResults'
import AIRecommender from './AIRecommender'

export default function Calculator({ title = 'Get Your Instant Roof Estimate', materialType = 'all' }: CalculatorProps) {
  // State management
  const [state, setState] = useState<CalculatorState>({
    country: 'US',
    zipCode: '',
    squareFootage: '',
    material: 'asphalt',
    removeOldRoof: 'yes',
    numLayers: 1,
    numStories: 1,
    roofPitch: 'medium',
    roofCondition: 'fair',
    hasChimneys: 0,
    hasSkylights: 0,
    hasValleys: 'moderate',
    urgency: 'flexible'
  })

  const [estimate, setEstimate] = useState<Estimate | null>(null)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [realTimePricing, setRealTimePricing] = useState<any>(null)
  const [loadingPrices, setLoadingPrices] = useState(false)
  const [priceError, setPriceError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [budget, setBudget] = useState(15000)
  
  const { user, isAuthenticated } = useAuth()
  const { saveEstimate } = useSavedEstimates()

  const currentCountry = countryConfigs[state.country]
  const materialOptions = getMaterialOptions(materialType)

  // Fetch real-time pricing when zip code and material change
  useEffect(() => {
    if (validateZipCode(state.zipCode, currentCountry) !== undefined || !state.material) return
    
    let cancelled = false
    setLoadingPrices(true)
    setPriceError(null)
    
    const fetchPricing = async () => {
      try {
        const response = await lumi.functions.invoke('getRoofingPrices', {
          method: 'POST',
          body: {
            zipCode: state.zipCode,
            material: state.material,
            squareFootage: parseFloat(state.squareFootage) || 2000
          }
        })
        
        if (!cancelled) {
          setRealTimePricing(response)
          console.log('Real-time pricing fetched:', response)
        }
      } catch (error: any) {
        if (!cancelled) {
          const errorMessage = error?.data?.error || 'Unable to fetch real-time pricing'
          setPriceError(errorMessage)
          console.error('Pricing API error:', errorMessage)
        }
      } finally {
        if (!cancelled) {
          setLoadingPrices(false)
        }
      }
    }
    
    fetchPricing()
    
    return () => {
      cancelled = true
    }
  }, [state.zipCode, state.material, state.squareFootage, currentCountry])

  const handleZipCodeChange = (value: string) => {
    const cleaned = state.country === 'US' ? value.replace(/\D/g, '').slice(0, 5) : value.toUpperCase().slice(0, 10)
    setState(prev => ({ ...prev, zipCode: cleaned }))
    if (touched.zipCode) {
      setErrors(prev => ({ ...prev, zipCode: validateZipCode(cleaned, currentCountry) }))
    }
  }

  const handleSquareFootageChange = (value: string) => {
    setState(prev => ({ ...prev, squareFootage: value }))
    if (touched.squareFootage) {
      setErrors(prev => ({ ...prev, squareFootage: validateSquareFootage(value) }))
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }))
    if (field === 'zipCode') {
      setErrors(prev => ({ ...prev, zipCode: validateZipCode(state.zipCode, currentCountry) }))
    } else if (field === 'squareFootage') {
      setErrors(prev => ({ ...prev, squareFootage: validateSquareFootage(state.squareFootage) }))
    }
  }

  const handleCalculate = () => {
    const zipError = validateZipCode(state.zipCode, currentCountry)
    const sqftError = validateSquareFootage(state.squareFootage)
    
    setTouched({ zipCode: true, squareFootage: true })
    setErrors({ zipCode: zipError, squareFootage: sqftError })

    if (zipError || sqftError) {
      if (zipError) {
        document.getElementById('zipCode')?.focus()
      } else if (sqftError) {
        document.getElementById('squareFootage')?.focus()
      }
      return
    }

    const result = calculateEstimate(state, currentCountry, realTimePricing)
    setEstimate(result)

    setTimeout(() => {
      const announcement = `Estimate calculated: $${Math.round(result.low).toLocaleString()} to $${Math.round(result.high).toLocaleString()}`
      const liveRegion = document.getElementById('calculator-live-region')
      if (liveRegion) {
        liveRegion.textContent = announcement
      }
    }, 100)
  }

  const handleSaveEstimate = async () => {
    if (!estimate || !user) return
    
    try {
      await saveEstimate({
        userId: user.userId,
        zipCode: state.zipCode,
        squareFootage: parseInt(state.squareFootage),
        material: state.material,
        estimateLow: estimate.low,
        estimateHigh: estimate.high,
        projectDetails: {
          removeOldRoof: state.removeOldRoof,
          numLayers: state.numLayers,
          numStories: state.numStories,
          roofPitch: state.roofPitch,
          roofCondition: state.roofCondition,
          hasChimneys: state.hasChimneys,
          hasSkylights: state.hasSkylights,
          hasValleys: state.hasValleys,
          urgency: state.urgency
        }
      })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      console.log('Estimate saved successfully')
    } catch (error) {
      console.error('Failed to save estimate:', error)
      alert('Failed to save estimate. Please try again.')
    }
  }

  const handleGeneratePDF = async () => {
    if (!estimate) return
    await downloadPDF(estimate, state, currentCountry, materialOptions)
  }

  const handleShareEmail = async (email: string) => {
    if (!estimate) throw new Error('No estimate available')
    await sharePDFViaEmail(estimate, state, currentCountry, materialOptions, email)
  }

  const hasErrors = errors.zipCode || errors.squareFootage

  return (
    <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-4xl w-full">
      <div id="calculator-live-region" className="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>
      
      <div className="flex items-center justify-center mb-6">
        <div className="bg-orange-500 p-3 rounded-full mr-3" aria-hidden="true">
          <CalcIcon className="text-white" size={28} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h2>
      </div>

      {loadingPrices && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center text-sm text-blue-800">
          <TrendingUp size={16} className="mr-2 animate-pulse" aria-hidden="true" />
          <span>Fetching real-time pricing data...</span>
        </div>
      )}
      {realTimePricing && !loadingPrices && (
        <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded-lg text-sm">
          <div className="flex items-center text-green-800 font-semibold mb-1">
            <TrendingUp size={16} className="mr-2" aria-hidden="true" />
            <span>Live Pricing Active</span>
          </div>
          <p className="text-green-700 text-xs">
            {realTimePricing.marketTrends} • Updated: {new Date(realTimePricing.lastUpdated).toLocaleString()}
          </p>
        </div>
      )}
      {priceError && !loadingPrices && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
          <AlertCircle size={16} className="inline mr-2" aria-hidden="true" />
          Using standard pricing (real-time data unavailable)
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleCalculate(); }} noValidate>
        <FormInputs
          country={state.country}
          setCountry={(country) => {
            setState(prev => ({ ...prev, country, zipCode: '' }))
            setErrors(prev => ({ ...prev, zipCode: undefined }))
            setTouched(prev => ({ ...prev, zipCode: false }))
          }}
          zipCode={state.zipCode}
          handleZipCodeChange={handleZipCodeChange}
          squareFootage={state.squareFootage}
          handleSquareFootageChange={handleSquareFootageChange}
          material={state.material}
          setMaterial={(material) => setState(prev => ({ ...prev, material }))}
          removeOldRoof={state.removeOldRoof}
          setRemoveOldRoof={(removeOldRoof) => setState(prev => ({ ...prev, removeOldRoof }))}
          numLayers={state.numLayers}
          setNumLayers={(numLayers) => setState(prev => ({ ...prev, numLayers }))}
          numStories={state.numStories}
          setNumStories={(numStories) => setState(prev => ({ ...prev, numStories }))}
          roofPitch={state.roofPitch}
          setRoofPitch={(roofPitch) => setState(prev => ({ ...prev, roofPitch }))}
          roofCondition={state.roofCondition}
          setRoofCondition={(roofCondition) => setState(prev => ({ ...prev, roofCondition }))}
          hasChimneys={state.hasChimneys}
          setHasChimneys={(hasChimneys) => setState(prev => ({ ...prev, hasChimneys }))}
          hasSkylights={state.hasSkylights}
          setHasSkylights={(hasSkylights) => setState(prev => ({ ...prev, hasSkylights }))}
          hasValleys={state.hasValleys}
          setHasValleys={(hasValleys) => setState(prev => ({ ...prev, hasValleys }))}
          urgency={state.urgency}
          setUrgency={(urgency) => setState(prev => ({ ...prev, urgency }))}
          errors={errors}
          touched={touched}
          handleBlur={handleBlur}
          currentCountry={currentCountry}
          materialOptions={materialOptions}
        />

        <button
          type="submit"
          disabled={!!(hasErrors && (touched.zipCode || touched.squareFootage))}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg shadow-lg mt-6"
          aria-label="Calculate detailed roof cost estimate"
        >
          Calculate My Detailed Roof Cost
        </button>
      </form>

      {estimate !== null && (
        <EstimateResults
          estimate={estimate}
          state={state}
          currentCountry={currentCountry}
          isAuthenticated={isAuthenticated}
          saveSuccess={saveSuccess}
          onGeneratePDF={handleGeneratePDF}
          onPrint={() => handlePrint(estimate, state, materialOptions)}
          onSave={handleSaveEstimate}
          onShareEmail={handleShareEmail}
        />
      )}

      <AIRecommender
        zipCode={state.zipCode}
        budget={estimate ? (estimate.low + estimate.high) / 2 : budget}
        onSelectMaterial={(material) => setState(prev => ({ ...prev, material }))}
      />
    </div>
  )
}
